import {
  inferredAlaskaHawaiiDomesticDepartureLeg,
  isAlaskaHawaiiDepartureToContiguous48,
} from "./akhiIcao";
import { money } from "./money";
import type { FlightLeg, MonthlyForm720Totals, QuarterlyTotals, Rates } from "./types";
import {
  isForeignForeignAirLeg,
  isInternationalFacilityRoute,
} from "./usInternationalAirLeg";

function quarterFor(isoDate: string): [number, number] {
  const parts = isoDate.split("-").map((x) => parseInt(x, 10));
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) throw new Error(`Invalid date: ${isoDate}`);
  const q = Math.floor((m - 1) / 3) + 1;
  return [y, q];
}

function yearMonthFromFlightDate(isoDate: string): [number, number] {
  const parts = isoDate.split("-").map((x) => parseInt(x, 10));
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) throw new Error(`Invalid date: ${isoDate}`);
  return [y, m];
}

/** The three calendar months in a calendar quarter (e.g. Q1 → Jan, Feb, Mar). */
export function calendarMonthsForQuarter(
  _year: number,
  quarter: number
): [number, number, number] {
  const first = (quarter - 1) * 3 + 1;
  return [first, first + 1, first + 2];
}

export function legsInCalendarMonth(
  legs: FlightLeg[],
  calendarYear: number,
  calendarMonth: number
): FlightLeg[] {
  return legs.filter((leg) => {
    const [y, m] = yearMonthFromFlightDate(leg.flightDate);
    return y === calendarYear && m === calendarMonth;
  });
}

/**
 * Passengers counted toward **IRS domestic segment** tax (IRS No. 26).
 * Uses `dom_pax` when set; on **foreign–foreign** legs (neither endpoint US domestic ICAO) returns
 * **0** unless `dom_pax` is set — `passenger_count` is not used on those routes. Otherwise uses
 * **`passenger_count`** (passenger name lists are not used for this headcount).
 */
export function effectiveDomesticSegmentPassengers(leg: FlightLeg): number {
  if (leg.excludeFrom720) return 0;
  if (leg.passengersDomesticSegmentTax !== null) {
    return Math.max(0, leg.passengersDomesticSegmentTax);
  }
  if (isForeignForeignAirLeg(leg.fromIcao, leg.toIcao)) {
    return 0;
  }
  return Math.max(0, leg.passengerCount);
}

/**
 * IRS No. 26 Alaska/Hawaii **domestic departure** headcount.
 * Uses `akhi_pax` / Manual Input when positive; otherwise, if the leg departs PA…/PH… and arrives
 * at another US airport, uses the same basis as domestic segment pax (`dom_pax` or `passenger_count`).
 * When positive and the leg lands at contiguous U.S. <code>K…</code>, domestic segment (IRS No. 26) may
 * still apply alongside this amount (IRS No. 27). Intra-PA/PH (or PH↔PA) uses this line without stacked domestic.
 */
export function effectiveAlaskaHawaiiDeparturePassengers(leg: FlightLeg): number {
  if (leg.excludeFrom720) return 0;
  const stored = Math.max(0, leg.alaskaHawaiiDomesticDeparturePassengers);
  if (stored > 0) return stored;
  if (!inferredAlaskaHawaiiDomesticDepartureLeg(leg.fromIcao, leg.toIcao)) return 0;
  return effectiveDomesticSegmentPassengers(leg);
}

/**
 * Passengers counted toward **IRS No. 27 international facility** on this leg.
 * Uses stored `intl_pax` only when the route is **US ↔ non-US**; foreign-only segments ignore it.
 * When this returns a positive value, domestic segment tax is not assessed on the same leg (mutually exclusive).
 */
export function effectiveInternationalFacilityPassengers(leg: FlightLeg): number {
  if (leg.excludeFrom720) return 0;
  if (!isInternationalFacilityRoute(leg.fromIcao, leg.toIcao)) return 0;
  return Math.max(0, leg.internationalFacilityPassengers);
}

function resolvedFuelGallons(leg: FlightLeg, lbsPerGallon: number): number | null {
  if (leg.fuelGallonsReported !== null) return leg.fuelGallonsReported;
  if (leg.fuelLbs !== null && lbsPerGallon > 0) return leg.fuelLbs / lbsPerGallon;
  return null;
}

/** Piecewise IRS Nos. 26–27 air excise (same rules as {@link legAirExcise}). */
export type LegAirExciseBreakdown = {
  percentageTax: number;
  domesticSegmentTax: number;
  internationalFacilityTax: number;
  alaskaHawaiiDepartureTax: number;
};

/**
 * IRS Nos. 26–27 components for one leg (charter %, domestic segment, intl facility, AK/HI departure).
 * **International facility** still suppresses domestic segment on that leg. **AK/HI departure** suppresses
 * domestic segment **except** Alaska/Hawaii → contiguous <code>K…</code> (e.g. Anchorage→Seattle), where
 * Form 720 instructions call for **both** domestic segment (No. 26) and AK/HI departure (No. 27).
 * @param applyPassengerTaxLines When false, omits passenger-based lines (charter % unchanged).
 */
export function legAirExciseBreakdown(
  leg: FlightLeg,
  rates: Rates,
  applyPassengerTaxLines: boolean = true
): LegAirExciseBreakdown {
  if (leg.excludeFrom720) {
    return {
      percentageTax: 0,
      domesticSegmentTax: 0,
      internationalFacilityTax: 0,
      alaskaHawaiiDepartureTax: 0,
    };
  }
  let percentageTax = 0;
  if (leg.charterOrFareAmountUsd > 0) {
    percentageTax = money(
      rates.airTransportationPercentage * leg.charterOrFareAmountUsd
    );
  }
  if (!applyPassengerTaxLines) {
    return {
      percentageTax,
      domesticSegmentTax: 0,
      internationalFacilityTax: 0,
      alaskaHawaiiDepartureTax: 0,
    };
  }
  const akP = effectiveAlaskaHawaiiDeparturePassengers(leg);
  const intlP = effectiveInternationalFacilityPassengers(leg);
  let domesticSegmentTax = 0;
  const paxSeg = effectiveDomesticSegmentPassengers(leg);
  const domesticWithAkToK =
    akP === 0 ||
    isAlaskaHawaiiDepartureToContiguous48(leg.fromIcao, leg.toIcao);
  if (
    paxSeg > 0 &&
    !leg.ruralAirportExempt &&
    intlP === 0 &&
    domesticWithAkToK
  ) {
    const segs = Math.max(1, leg.segmentsDomestic);
    domesticSegmentTax = money(
      segs * paxSeg * rates.domesticSegmentUsdPerPassengerPerSegment
    );
  }
  let internationalFacilityTax = 0;
  if (intlP > 0) {
    internationalFacilityTax = money(
      intlP * rates.internationalFacilityUsdPerPerson
    );
  }
  let alaskaHawaiiDepartureTax = 0;
  if (akP > 0) {
    alaskaHawaiiDepartureTax = money(
      akP * rates.alaskaHawaiiDomesticDepartureUsdPerPerson
    );
  }
  return {
    percentageTax,
    domesticSegmentTax,
    internationalFacilityTax,
    alaskaHawaiiDepartureTax,
  };
}

/**
 * IRS Nos. 26 + 27 air excise for one leg (same rules as quarterly aggregation; no fuel).
 * @param applyPassengerTaxLines When false, omits domestic segment, intl facility, and AK-HI departure lines (charter % unchanged).
 */
export function legAirExcise(
  leg: FlightLeg,
  rates: Rates,
  applyPassengerTaxLines: boolean = true
): number {
  const b = legAirExciseBreakdown(leg, rates, applyPassengerTaxLines);
  return money(
    b.percentageTax +
      b.domesticSegmentTax +
      b.internationalFacilityTax +
      b.alaskaHawaiiDepartureTax
  );
}

type Form720ExciseBucket = {
  pct: number;
  seg: number;
  intl: number;
  akhi: number;
  fuel: number;
};

/** IRS 26+27 line items + log fuel gallons for a leg subset (same rules as quarterly aggregation). */
function addLegForm720ExciseAndFuel(
  leg: FlightLeg,
  rates: Rates,
  b: Form720ExciseBucket,
  applyPassengerTaxLines: boolean = true
): void {
  if (leg.excludeFrom720) {
    const fg = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
    if (fg !== null) b.fuel += fg;
    return;
  }
  if (leg.charterOrFareAmountUsd > 0) {
    b.pct += money(
      rates.airTransportationPercentage * leg.charterOrFareAmountUsd
    );
  }
  if (applyPassengerTaxLines) {
    const akP = effectiveAlaskaHawaiiDeparturePassengers(leg);
    const intlP = effectiveInternationalFacilityPassengers(leg);
    const paxSeg = effectiveDomesticSegmentPassengers(leg);
    const domesticWithAkToK =
      akP === 0 ||
      isAlaskaHawaiiDepartureToContiguous48(leg.fromIcao, leg.toIcao);
    if (
      paxSeg > 0 &&
      !leg.ruralAirportExempt &&
      intlP === 0 &&
      domesticWithAkToK
    ) {
      const segs = Math.max(1, leg.segmentsDomestic);
      b.seg += money(
        segs * paxSeg * rates.domesticSegmentUsdPerPassengerPerSegment
      );
    }
    if (intlP > 0) {
      b.intl += money(intlP * rates.internationalFacilityUsdPerPerson);
    }
    if (akP > 0) {
      b.akhi += money(akP * rates.alaskaHawaiiDomesticDepartureUsdPerPerson);
    }
  }
  const fg = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
  if (fg !== null) b.fuel += fg;
}

export type AirExciseTaxComponents = {
  irs26PercentageTax: number;
  irs26DomesticSegmentTax: number;
  irs27InternationalFacilityTax: number;
  irs26AkHiDepartureTax: number;
  fuelGallonsFromLog: number;
};

export function airExciseTaxComponentsForLegs(
  legs: FlightLeg[],
  rates: Rates,
  applyPassengerTaxLines: boolean = true
): AirExciseTaxComponents {
  const b: Form720ExciseBucket = { pct: 0, seg: 0, intl: 0, akhi: 0, fuel: 0 };
  for (const leg of legs) {
    addLegForm720ExciseAndFuel(leg, rates, b, applyPassengerTaxLines);
  }
  return {
    irs26PercentageTax: money(b.pct),
    irs26DomesticSegmentTax: money(b.seg),
    irs27InternationalFacilityTax: money(b.intl),
    irs26AkHiDepartureTax: money(b.akhi),
    fuelGallonsFromLog: money(b.fuel),
  };
}

/** Sum of {@link legAirExcise} over legs (0 for <code>exclude_from_720</code>). */
export function sumLegAirExciseForLegs(
  legs: FlightLeg[],
  rates: Rates,
  applyPassengerTaxLines: boolean = true
): number {
  let n = 0;
  for (const leg of legs) {
    n = money(n + legAirExcise(leg, rates, applyPassengerTaxLines));
  }
  return n;
}

export type AggregateOptions = {
  /** When set, used for personal reimbursement bucket instead of <code>leg.personalUse</code> alone. */
  isPersonalLeg?: (leg: FlightLeg) => boolean;
};

function compareQuarterKeysDesc(a: string, b: string): number {
  const [ay, aqS] = a.split("-Q");
  const [by, bqS] = b.split("-Q");
  const ayN = parseInt(ay ?? "", 10);
  const byN = parseInt(by ?? "", 10);
  const aqN = parseInt(aqS ?? "", 10);
  const bqN = parseInt(bqS ?? "", 10);
  if (byN !== ayN) return byN - ayN;
  return bqN - aqN;
}

/**
 * Quarter buckets for Form 720 / Summary tab. Same per-leg rules as {@link legAirExciseBreakdown}.
 * **International facility** vs domestic segment: mutually exclusive. **AK/HI departure to contiguous
 * <code>K…</code>** (e.g. PANC→KSEA) stacks **domestic segment (No. 26)** with **AK/HI departure (No. 27)**;
 * intra-PA/PH AK legs use No. 27 only for that headcount. Human-readable labels: <code>passengerExciseRouteRuleLabel</code>.
 */
export function aggregateByQuarter(
  legs: FlightLeg[],
  rates: Rates,
  opts?: AggregateOptions
): Map<string, QuarterlyTotals> {
  const isPersonal = opts?.isPersonalLeg ?? ((leg: FlightLeg) => leg.personalUse);
  type Bucket = Form720ExciseBucket & { legs: number; personalReimburse: number };

  const buckets = new Map<string, Bucket>();

  function key(y: number, q: number) {
    return `${y}-Q${q}`;
  }

  for (const leg of legs) {
    const [y, q] = quarterFor(leg.flightDate);
    const k = key(y, q);
    let b = buckets.get(k);
    if (!b) {
      b = {
        pct: 0,
        seg: 0,
        intl: 0,
        akhi: 0,
        fuel: 0,
        legs: 0,
        personalReimburse: 0,
      };
      buckets.set(k, b);
    }
    b.legs += 1;

    if (leg.excludeFrom720) {
      const fg = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
      if (fg !== null) b.fuel += fg;
      continue;
    }

    if (isPersonal(leg)) {
      b.personalReimburse += legAirExcise(leg, rates);
    }

    addLegForm720ExciseAndFuel(leg, rates, b);
  }

  const sortedKeys = [...buckets.keys()].sort(compareQuarterKeysDesc);
  const out = new Map<string, QuarterlyTotals>();

  for (const k of sortedKeys) {
    const b = buckets.get(k)!;
    const [ys, qs] = k.split("-Q");
    const year = parseInt(ys, 10);
    const quarter = parseInt(qs, 10);
    out.set(k, {
      year,
      quarter,
      irs26PercentageTax: money(b.pct),
      irs26DomesticSegmentTax: money(b.seg),
      irs27InternationalFacilityTax: money(b.intl),
      irs26AkHiDepartureTax: money(b.akhi),
      fuelGallonsFromLog: money(b.fuel),
      legCount: b.legs,
      personalReimbursementAirExcise: money(b.personalReimburse),
    });
  }

  return out;
}

/**
 * Same rules as {@link aggregateByQuarter}, but splits each quarter into **three calendar months**
 * (Form 720 Part II–style record). Liability is allocated by each leg’s <code>flightDate</code>
 * month — confirm timing with current Form 720 instructions if your position differs.
 */
export function aggregateExciseByQuarterMonths(
  legs: FlightLeg[],
  rates: Rates,
  opts?: AggregateOptions
): Map<string, MonthlyForm720Totals[]> {
  const isPersonal = opts?.isPersonalLeg ?? ((leg: FlightLeg) => leg.personalUse);
  type MBucket = Form720ExciseBucket & { legs: number; personalReimburse: number };

  const outer = new Map<string, [MBucket, MBucket, MBucket]>();

  const emptyBucket = (): MBucket => ({
    pct: 0,
    seg: 0,
    intl: 0,
    akhi: 0,
    fuel: 0,
    legs: 0,
    personalReimburse: 0,
  });

  for (const leg of legs) {
    const [y, m] = yearMonthFromFlightDate(leg.flightDate);
    const q = Math.floor((m - 1) / 3) + 1;
    const slot = (m - 1) % 3;
    const k = `${y}-Q${q}`;
    if (!outer.has(k)) {
      outer.set(k, [emptyBucket(), emptyBucket(), emptyBucket()]);
    }
    const triplet = outer.get(k)!;
    const b = triplet[slot]!;

    b.legs += 1;

    if (leg.excludeFrom720) {
      const fg = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
      if (fg !== null) b.fuel += fg;
      continue;
    }

    if (isPersonal(leg)) {
      b.personalReimburse += legAirExcise(leg, rates);
    }

    addLegForm720ExciseAndFuel(leg, rates, b);
  }

  const sortedKeys = [...outer.keys()].sort(compareQuarterKeysDesc);
  const out = new Map<string, MonthlyForm720Totals[]>();

  for (const k of sortedKeys) {
    const triplet = outer.get(k)!;
    const [ys, qs] = k.split("-Q");
    const year = parseInt(ys, 10);
    const quarter = parseInt(qs, 10);
    const months = calendarMonthsForQuarter(year, quarter);

    const row = (slot: number): MonthlyForm720Totals => {
      const b = triplet[slot]!;
      const calendarMonth = months[slot]!;
      return {
        calendarYear: year,
        calendarMonth,
        irs26PercentageTax: money(b.pct),
        irs26DomesticSegmentTax: money(b.seg),
        irs27InternationalFacilityTax: money(b.intl),
        irs26AkHiDepartureTax: money(b.akhi),
        fuelGallonsFromLog: money(b.fuel),
        legCount: b.legs,
        personalReimbursementAirExcise: money(b.personalReimburse),
      };
    };

    out.set(k, [row(0), row(1), row(2)]);
  }

  return out;
}

/** Sum several calendar quarters into one totals row (Summary multi-select). */
export function combineQuarterlyTotals(
  parts: QuarterlyTotals[]
): QuarterlyTotals | null {
  if (parts.length === 0) return null;
  let pct = 0;
  let seg = 0;
  let intl = 0;
  let akhi = 0;
  let fuel = 0;
  let legs = 0;
  let personal = 0;
  for (const t of parts) {
    pct += t.irs26PercentageTax;
    seg += t.irs26DomesticSegmentTax;
    intl += t.irs27InternationalFacilityTax;
    akhi += t.irs26AkHiDepartureTax;
    fuel += t.fuelGallonsFromLog;
    legs += t.legCount;
    personal += t.personalReimbursementAirExcise;
  }
  return {
    year: parts[0]!.year,
    quarter: parts[0]!.quarter,
    irs26PercentageTax: money(pct),
    irs26DomesticSegmentTax: money(seg),
    irs27InternationalFacilityTax: money(intl),
    irs26AkHiDepartureTax: money(akhi),
    fuelGallonsFromLog: money(fuel),
    legCount: legs,
    personalReimbursementAirExcise: money(personal),
  };
}
